import { FolderPlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import type { UploadPhase } from "common";
import ProgressBar from "@via/components/progress-bar/progress-bar.tsx";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { classNames } from "@via/utils/helpers.tsx";

interface FileUploaderProps {
  maxFiles?: number;
  maxFileSize?: number; // In bytes
  acceptedTypes?: string[];
  onUpload?: (files: File[]) => void;
  onError?: (error: string) => void;
  onCancel?: () => void; // Cancellation function for all in-progress uploads
  isLoading?: boolean;
  progressPercentageList: {
    filename: string;
    progress: number;
    phase?: UploadPhase;
    /** #1024: the server refused this file; shown on its row instead of the progress bar. */
    error?: string;
  }[];
  prefix?: string;
  isDisabled?: boolean;
  containerClassName?: string;
  onPrefixChange?: (newPrefix: string) => void;
  showFolderCreation?: boolean;
  fullDestinationPath?: string;
  /** Callback when user confirms termination - parent should close modal */
  onConfirmTerminate?: () => void;
}

interface SelectedFile {
  file: File;
  progress: number;
}

export interface FileUploaderHandle {
  checkBeforeClose: () => Promise<boolean>;
}

const FileUploader = forwardRef<FileUploaderHandle, FileUploaderProps>(
  (
    {
      maxFiles = 5,
      maxFileSize = 120 * 1024 * 1024 * 1024, // 120 GB default
      acceptedTypes,
      onUpload,
      onCancel,
      isLoading,
      progressPercentageList,
      prefix = "",
      isDisabled = false,
      containerClassName = "",
      onPrefixChange,
      showFolderCreation = false,
      fullDestinationPath = "",
      onConfirmTerminate,
    },
    ref
  ) => {
    const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [currentPrefix, setCurrentPrefix] = useState<string>(prefix);
    const [displayDestinationPath, setDisplayDestinationPath] =
      useState<string>(fullDestinationPath);
    const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false);
    const [newFolderName, setNewFolderName] = useState<string>("");
    const [folderInputError, setFolderInputError] = useState<{
      message: string;
    } | null>(null);
    const [previousPrefix, setPreviousPrefix] = useState<string>(prefix);
    const [prefixAtUploadStart, setPrefixAtUploadStart] =
      useState<string>(prefix);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    // Sync currentPrefix with prefix prop changes
    useEffect(() => {
      setCurrentPrefix(prefix);
      setPreviousPrefix(prefix);
    }, [prefix]);

    // Sync displayDestinationPath with fullDestinationPath prop changes
    useEffect(() => {
      if (fullDestinationPath) {
        setDisplayDestinationPath(fullDestinationPath);
      }
    }, [fullDestinationPath]);

    // Detect successful upload completion and notify parent of prefix change
    useEffect(() => {
      if (
        !isLoading &&
        selectedFiles.length > 0 &&
        progressPercentageList.length > 0
      ) {
        const allUploadsComplete = progressPercentageList.every(
          progress => progress.progress === 100
        );
        // Check if all uploaded files have progress entries using the prefix from when upload started
        const allFilesHaveProgress = selectedFiles.every(selectedFile => {
          const fileProgress = progressPercentageList.find(
            progress =>
              progress.filename ===
              `${prefixAtUploadStart}${selectedFile.file.name}`
          );
          return fileProgress !== undefined;
        });

        // If upload completed successfully and prefix has changed, notify parent
        if (
          allUploadsComplete &&
          allFilesHaveProgress &&
          currentPrefix !== previousPrefix &&
          onPrefixChange
        ) {
          onPrefixChange(currentPrefix);
          setPreviousPrefix(currentPrefix);
        }
      }
    }, [
      isLoading,
      progressPercentageList,
      selectedFiles,
      prefixAtUploadStart,
      currentPrefix,
      previousPrefix,
      onPrefixChange,
    ]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      processFiles(files);

      // Reset input value so that selecting same file again will re-trigger change event
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const files = Array.from(event.dataTransfer.files || []);
      processFiles(files);
    };

    const sanitizeFilename = (name: string): string => name.replace(/ /g, "_");

    const processFiles = (files: File[]) => {
      const newFiles: SelectedFile[] = [];

      setErrorMessages(prev =>
        prev.filter(msg => !msg.includes("Please upload a smaller file."))
      );

      files.forEach(file => {
        // If maxFiles is 1, and we already have a file, replace it instead of adding
        if (maxFiles === 1 && selectedFiles.length > 0) {
          setSelectedFiles([]); // Clear existing file
        } else if (selectedFiles.length + newFiles.length >= maxFiles) {
          setErrorMessages(prev => [
            ...new Set([
              ...prev,
              `You can only upload up to ${maxFiles} files.`,
            ]),
          ]);
          return;
        }

        const sanitizedName = sanitizeFilename(file.name);
        const sanitizedFile =
          sanitizedName !== file.name
            ? new File([file], sanitizedName, {
                type: file.type,
                lastModified: file.lastModified,
              })
            : file;

        const fileExtension = sanitizedFile.name
          .split(".")
          .pop()
          ?.toLowerCase();

        if (
          acceptedTypes &&
          !acceptedTypes.some(
            type => type.toLowerCase() === `.${fileExtension}`
          )
        ) {
          setErrorMessages(prev => [
            ...new Set([
              ...prev,
              `File type not allowed: ${sanitizedFile.name}`,
            ]),
          ]);
          return;
        }

        if (sanitizedFile.size > maxFileSize) {
          setErrorMessages(prev => [
            ...new Set([
              ...prev,
              `The file "${sanitizedFile.name}" exceeds the maximum allowed size of ${maxFileSize / (1024 * 1024)} MB. Please upload a smaller file.`,
            ]),
          ]);
          return;
        }

        newFiles.push({ file: sanitizedFile, progress: 0 });
      });

      setSelectedFiles(prev => [...prev, ...newFiles]);
    };

    const handleUpload = () => {
      if (onUpload) {
        // Track the prefix used when upload starts
        setPrefixAtUploadStart(currentPrefix);
        const filesToUpload = selectedFiles.map(({ file }) => {
          const prefixedFile = new File(
            [file],
            `${currentPrefix}${file.name}`,
            {
              type: file.type,
              lastModified: file.lastModified,
            }
          );
          return prefixedFile;
        });
        onUpload(filesToUpload);
      }
    };

    const handleDiscardFile = (file: File) => {
      // Check if file is currently uploading
      const fileProgress = progressPercentageList.find(
        progress => progress.filename === `${currentPrefix}${file.name}`
      );

      // Prevent removing files that are currently uploading
      // Users must use "Cancel Upload" button to cancel all uploads
      if (
        isLoading &&
        fileProgress &&
        fileProgress.progress > 0 &&
        fileProgress.progress < 100
      ) {
        // Don't allow removing files during active upload
        return;
      }

      // If not uploading or upload completed, just remove from list
      setSelectedFiles(prev => prev.filter(f => f.file !== file));

      // Remove any error messages related to the discarded file
      setErrorMessages(prev =>
        prev.filter(
          msg =>
            !msg.includes(file.name) &&
            !msg.includes(`You can only upload up to ${maxFiles} files.`)
        )
      );
    };

    const handleFileSelectionClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const handleCancel = () => {
      // If upload is in progress, cancel it first
      if (isLoading && onCancel) {
        onCancel();
        // Note: selectedFiles and errors will be cleared by the parent component
        // after cancellation completes, so we don't clear them here during active upload
        return;
      }
      // If not uploading, just clear selected files and errors
      setSelectedFiles([]);
      setErrorMessages([]);
    };

    const handleCreateFolderClick = () => {
      setIsCreatingFolder(true);
      setTimeout(() => {
        folderInputRef.current?.focus();
      }, 0);
    };

    const setFolderError = (message: string) => {
      setFolderInputError({ message });
      return false;
    };

    const validateFolderInput = (value: string) => {
      const trimmedValue = value.trim();
      if (trimmedValue.startsWith("/")) {
        return setFolderError("Folder path cannot start with '/'.");
      }
      if (trimmedValue.endsWith("/") && trimmedValue.length > 0) {
        return setFolderError("Folder path cannot end with '/'.");
      }
      if (/\/{2,}/.test(trimmedValue)) {
        return setFolderError("Folder path cannot contain consecutive '/'.");
      }
      setFolderInputError(null);
      return true;
    };

    const handleFolderNameChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = event.target.value;
      // Prevent spaces
      if (!value.includes(" ")) {
        setNewFolderName(value);
        validateFolderInput(value);
      }
    };

    const handleFolderNameSubmit = () => {
      if (!validateFolderInput(newFolderName)) {
        return;
      }
      const trimmedName = newFolderName.trim();
      if (trimmedName === "") {
        setErrorMessages(prev => [
          ...new Set([...prev, "Folder name cannot be empty."]),
        ]);
        return;
      }

      // Construct new prefix (for upload logic - relative path)
      const normalizedPrefix = currentPrefix.endsWith("/")
        ? currentPrefix.slice(0, -1)
        : currentPrefix;
      let newPrefix = normalizedPrefix
        ? `${normalizedPrefix}/${trimmedName}`
        : trimmedName;

      // Ensure new prefix ends with a slash
      if (!newPrefix.endsWith("/")) {
        newPrefix = `${newPrefix}/`;
      }

      // Update internal prefix for upload logic
      setCurrentPrefix(newPrefix);

      // Update display destination path (full path with bucket name)
      const normalizedDisplayPath = displayDestinationPath.endsWith("/")
        ? displayDestinationPath.slice(0, -1)
        : displayDestinationPath;
      const newDisplayPath = normalizedDisplayPath
        ? `${normalizedDisplayPath}/${trimmedName}/`
        : `${trimmedName}/`;

      setDisplayDestinationPath(newDisplayPath);
      setIsCreatingFolder(false);
      setNewFolderName("");
      setFolderInputError(null);
    };

    const handleFolderNameCancel = () => {
      setIsCreatingFolder(false);
      setNewFolderName("");
      setFolderInputError(null);
    };

    const handleFolderNameKeyPress = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === "Enter") {
        handleFolderNameSubmit();
      } else if (event.key === "Escape") {
        handleFolderNameCancel();
      }
    };

    // Expose method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        checkBeforeClose: async (): Promise<boolean> => {
          if (isLoading && onCancel) {
            // Show confirmation dialog for all files
            setShowCancelConfirmation(true);
            return false; // Prevent closing
          }
          return true; // Allow closing
        },
      }),
      [isLoading, onCancel]
    );

    const handleConfirmTerminate = () => {
      // Cancel all uploads
      if (onCancel) {
        onCancel();
      }
      setShowCancelConfirmation(false);
      // Clear selected files after cancellation
      setSelectedFiles([]);
      setErrorMessages([]);
      // Notify parent to close modal
      if (onConfirmTerminate) {
        onConfirmTerminate();
      }
    };

    const handleContinueUpload = () => {
      setShowCancelConfirmation(false);
      // Upload continues, modal stays open
    };

    return (
      <div className="mx-auto flex flex-col space-y-4 relative">
        {/* Inline Confirmation Dialog Overlay */}
        {showCancelConfirmation && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-95 rounded-lg">
            <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Terminate Transfer?
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                Would you like to terminate the transfer?
              </p>
              <p className="text-xs text-gray-500 mb-6">
                All in-progress uploads will be cancelled and any partial data
                will be cleared.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleContinueUpload}
                  className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  Continue Upload
                </button>
                <button
                  type="button"
                  onClick={handleConfirmTerminate}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
                  Terminate
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Destination Display */}
        {showFolderCreation && (
          <div className="text-sm text-gray-700">
            <span className="font-semibold">Destination:</span>{" "}
            {displayDestinationPath || currentPrefix}
          </div>
        )}

        {/* Create Folder UI */}
        {showFolderCreation &&
          (!isCreatingFolder ? (
            <button
              type="button"
              onClick={handleCreateFolderClick}
              disabled={isDisabled}
              className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
              <FolderPlusIcon className="h-5 w-5" />
              Create New Folder
            </button>
          ) : (
            <div className="flex items-start gap-2">
              <div className="flex flex-1 flex-col gap-1">
                <input
                  ref={folderInputRef}
                  type="text"
                  value={newFolderName}
                  onChange={handleFolderNameChange}
                  onKeyDown={handleFolderNameKeyPress}
                  placeholder="Enter folder name"
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                {folderInputError && (
                  <span className="text-xs text-rose-500">
                    {folderInputError.message}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleFolderNameSubmit}
                disabled={newFolderName.trim() === "" || !!folderInputError}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed">
                Create
              </button>
              <button
                type="button"
                onClick={handleFolderNameCancel}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          ))}

        <div
          className={classNames(
            isLoading || isDisabled ? "pointer-events-none" : "",
            "w-200 flex h-40 cursor-pointer flex-col items-center gap-6 border-2 border-dashed border-gray-300 bg-gray-50 p-4 hover:bg-gray-100",
            containerClassName
          )}
          onClick={handleFileSelectionClick}
          onDrop={handleFileDrop}
          onDragOver={e => e.preventDefault()}>
          <input
            type="file"
            multiple
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={acceptedTypes && acceptedTypes.join(",")}
          />
          <p className="text-center font-semibold text-gray-600 pointer-events-none">
            Drag and Drop Files Here
          </p>
          <button
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            type="button"
            disabled={isDisabled}>
            Browse Files
          </button>
        </div>

        {errorMessages.length > 0 && (
          <div className="rounded border border-red-400 bg-red-100 p-2 text-red-700">
            <ul>
              {errorMessages.map((error, idx) => (
                <li key={`file-upload-error-${error}-${idx}`}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 space-y-2">
          {selectedFiles.map((selectedFile, idx) => {
            // Find matching progress bar for the file
            const fileProgress = progressPercentageList.find(
              progress =>
                progress.filename ===
                `${currentPrefix}${selectedFile.file.name}`
            );

            return (
              <div
                key={`selected-file${selectedFile.file.name}-${idx}`}
                className="flex flex-col rounded bg-white p-2 shadow-sm hover:bg-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      {selectedFile.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    className="cursor-pointer"
                    onClick={() => handleDiscardFile(selectedFile.file)}>
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>

                {fileProgress?.error ? (
                  <p
                    role="alert"
                    data-testid="file-upload-row-error"
                    className="mt-1 text-xs text-red-600">
                    {fileProgress.error}
                  </p>
                ) : fileProgress ? (
                  <div className="mt-1">
                    {fileProgress.phase ? (
                      <p className="mb-1 text-xs text-gray-500">
                        {fileProgress.phase === "sending_to_cluster"
                          ? "Sending to cluster"
                          : "Uploading"}
                      </p>
                    ) : null}
                    <ProgressBar progress={fileProgress.progress} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-4 flex flex-shrink-0 justify-end py-4">
            <button
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleCancel}
              disabled={false}>
              {isLoading ? "Cancel Upload" : "Cancel"}
            </button>
            <button
              className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={handleUpload}
              disabled={isLoading}>
              <Spinner loading={isLoading}>Upload</Spinner>
            </button>
          </div>
        )}
      </div>
    );
  }
);

FileUploader.displayName = "FileUploader";

export default FileUploader;
